import { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  confirmEmailUnsubscribe,
  validateEmailUnsubscribe,
} from '../../api/emailUnsubscribe';
import './EmailUnsubscribePage.css';

type PageState =
  | { kind: 'loading' }
  | { kind: 'invalid'; message: string }
  | { kind: 'confirm'; email: string }
  | { kind: 'submitting'; email: string }
  | { kind: 'done'; email: string }
  | { kind: 'error'; email: string; message: string };

function EmailUnsubscribePage() {
  const [searchParams] = useSearchParams();
  const email = (searchParams.get('email') || '').trim().toLowerCase();
  const team = (searchParams.get('team') || '').trim();
  const token = (searchParams.get('token') || '').trim();
  const messageId = (searchParams.get('mid') || '').trim() || null;

  const params = useMemo(
    () => ({ email, team, token, messageId }),
    [email, team, token, messageId],
  );

  const [state, setState] = useState<PageState>({ kind: 'loading' });

  useEffect(() => {
    let cancelled = false;

    const run = async () => {
      if (!email || !token) {
        if (!cancelled) {
          setState({
            kind: 'invalid',
            message: 'This unsubscribe link is missing required details.',
          });
        }
        return;
      }

      try {
        const result = await validateEmailUnsubscribe(params);
        if (!cancelled) {
          setState({ kind: 'confirm', email: result.email || email });
        }
      } catch (err: any) {
        if (!cancelled) {
          setState({
            kind: 'invalid',
            message: err?.message || 'Invalid unsubscribe link',
          });
        }
      }
    };

    void run();
    return () => {
      cancelled = true;
    };
  }, [email, token, params]);

  const onConfirm = async () => {
    if (state.kind !== 'confirm') return;
    setState({ kind: 'submitting', email: state.email });
    try {
      const result = await confirmEmailUnsubscribe(params);
      setState({ kind: 'done', email: result.email || state.email });
    } catch (err: any) {
      setState({
        kind: 'error',
        email: state.email,
        message: err?.message || 'Could not unsubscribe. Please try again.',
      });
    }
  };

  return (
    <main className="email-unsub-page">
      <div className="email-unsub-shell">
        <p className="email-unsub-brand">BotSpot</p>

        {state.kind === 'loading' && (
          <>
            <h1>Checking your unsubscribe link</h1>
            <p className="email-unsub-lede">One moment.</p>
          </>
        )}

        {state.kind === 'invalid' && (
          <>
            <h1>This unsubscribe link is not valid</h1>
            <p className="email-unsub-lede">{state.message}</p>
            <p className="email-unsub-muted">
              If you still get marketing emails, reply to one and ask to be removed.
            </p>
            <Link className="email-unsub-secondary" to="/">
              Back to BotSpot
            </Link>
          </>
        )}

        {(state.kind === 'confirm' || state.kind === 'submitting') && (
          <>
            <h1>Unsubscribe from marketing emails</h1>
            <p className="email-unsub-lede">
              Stop marketing emails to <strong>{state.email}</strong>?
            </p>
            <p className="email-unsub-muted">
              You will still get transactional emails like receipts and account notices.
            </p>
            <button
              type="button"
              className="email-unsub-primary"
              onClick={onConfirm}
              disabled={state.kind === 'submitting'}
            >
              {state.kind === 'submitting' ? 'Unsubscribing…' : 'Unsubscribe me'}
            </button>
          </>
        )}

        {state.kind === 'done' && (
          <>
            <h1>You are unsubscribed</h1>
            <p className="email-unsub-lede">
              <strong>{state.email}</strong> will no longer receive BotSpot marketing emails.
            </p>
            <p className="email-unsub-muted">
              Transactional emails like receipts and account notices may still arrive.
            </p>
            <Link className="email-unsub-secondary" to="/">
              Back to BotSpot
            </Link>
          </>
        )}

        {state.kind === 'error' && (
          <>
            <h1>Something went wrong</h1>
            <p className="email-unsub-lede">{state.message}</p>
            <button
              type="button"
              className="email-unsub-primary"
              onClick={() => setState({ kind: 'confirm', email: state.email })}
            >
              Try again
            </button>
          </>
        )}
      </div>
    </main>
  );
}

export default EmailUnsubscribePage;
